import { type AxiosRequestConfig } from "axios";

import { defaultMentionsFields } from "shared/constants";
import DateRange from "shared/helpers/DateRange";
import { getMediaForStream, getTypesForStream } from "shared/helpers/media";
import { type FetchMentionsParams } from "shared/services/fetch/fetchMentions";
import { type FetchServiceOptions } from "shared/services/fetch/FetchServiceFactory";
import { type Nullable, type Stream } from "shared/types";
import { type Mention, type MentionType } from "shared/types/mentions";

export interface StreamRequestOptions {
  mentions?: Nullable<Mention[]>;
  options?: FetchServiceOptions;
  stream: Stream | { id: string | number };
}

const buildRequestOptions = ({
  mentions,
  options = { range: DateRange.lastYear() },
  stream,
}: StreamRequestOptions): Nullable<AxiosRequestConfig<FetchMentionsParams>> => {
  const fullStream = stream as Stream;

  if (
    !getMediaForStream(fullStream).length &&
    !getTypesForStream(fullStream).length
  ) {
    return null;
  }

  const range = { ...options.range };

  const fields = options.allFields
    ? null
    : options.fields || defaultMentionsFields;

  let timestampProp = "timestamp";

  // convert timestamps to milliseconds if required
  if (options.useMilliseconds) {
    timestampProp = "timestamp_milliseconds";
    range.before *= 1000;
    range.after *= 1000;
  }

  const localMentions = mentions || [];

  const latestMention = options.loadLatestMentions
    ? localMentions[0]
    : localMentions.slice(-1)[0];

  return {
    ...(options.timeout ? { timeout: options.timeout } : {}),
    data: {
      ...(options.filter_most_read_sources
        ? { filter_most_read_sources: options.filter_most_read_sources }
        : {}),
      after: range.after,
      // use the last mention timestamp unless we are paginating or loading fresh
      before:
        options.keepAlreadyLoadedMentions &&
        !options.page &&
        !options.loadLatestMentions &&
        localMentions.length
          ? (latestMention?.[timestampProp as keyof Mention] as number)
          : range.before,
      excerpt_radius: options.excerpt_radius || 60,
      ...(options.collapse_syndicated ? { collapse_syndicated: true } : {}),
      ...(options.bundle_broadcast ? { bundle_broadcast: true } : {}),
      ...(fields ? { fields } : {}),
      limit: options.limit ? options.limit : 10,
      ...(options.sort_options ? { sort_options: options.sort_options } : {}),
      ...(options.sortBy ? { sort_by: options.sortBy } : {}),
      ...(options.sortOrder ? { sort_order: options.sortOrder } : {}),
      ...(options.page ? { page: options.page } : {}),
      ...(options.media ? { media: options.media } : {}),
      ...(options.case_sensitive !== undefined
        ? { case_sensitive: options.case_sensitive }
        : {}),
      ...(options.syndicationKeys
        ? { excluded_syndication_keys: options.syndicationKeys }
        : {}),
      ...(options.useMilliseconds
        ? { timestamps_in_milliseconds: options.useMilliseconds }
        : {}),
      ...(options.types
        ? { types: options.types as MentionType[] }
        : { types: getTypesForStream(fullStream) }),
      ...(options.includeBookmarkSyndication !== undefined
        ? { include_bookmark_syndication: options.includeBookmarkSyndication }
        : {}),
      ...(options.search ? options.search : {}),
      ...(options.filterSetId ? { filter_set_id: options.filterSetId } : {}),
    },
    ...(options.signal ? { signal: options.signal } : {}),
    ...(options.whole_text ? { whole_text: options.whole_text } : {}),
  };
};

export default buildRequestOptions;
